import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Download, Check, Sparkles } from 'lucide-react';
import { featurePages } from '../data/knowledgeHub';
import { Helmet } from 'react-helmet-async';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const FeatureArticle = () => {
  const { slug } = useParams();
  const feature = featurePages.find(f => f.slug === slug);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  if (!feature) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 container text-center">
          <h1 className="text-3xl font-bold mb-4">Feature Not Found</h1>
          <Link to="/feature" className="text-orange-600 hover:underline">View All Features</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{feature.title} | TypeIt4U</title>
        <meta name="description" content={feature.description} />
        <meta name="keywords" content={feature.keywords?.join(', ')} />
        <link rel="canonical" href={`https://typeit4u.com/feature/${feature.slug}`} />
      </Helmet>

      <Navbar />
      
      {/* Breadcrumb */}
      <section className="pt-28 pb-4 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <Link to="/feature" className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            All Features
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="pb-12 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center"
          >
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)'
              }}
            >
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              {feature.title}
            </h1>
            <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
            
            {feature.hero && (
              <div className="bg-white rounded-xl p-6 shadow-lg border border-orange-100 max-w-2xl mx-auto">
                <p className="text-xl font-medium" style={{ color: '#0A0A0A' }}>{feature.hero}</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Benefits */}
            {feature.benefits && (
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={springConfig}
                className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-100"
              >
                <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                  Key Benefits
                </h2>
                <ul className="space-y-4">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* How It Works */}
            {feature.howItWorks && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={springConfig}
                className="bg-white rounded-2xl p-8 border border-gray-200"
              >
                <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                  How It Works
                </h2>
                <div className="space-y-4">
                  {feature.howItWorks.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <span 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-gray-700 pt-1">{step}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 text-center bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-10 border border-orange-100"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Try {feature.title.split(' - ')[0]} Now
            </h3>
            <p className="text-gray-600 mb-6">
              Download TypeIt4U and experience this feature for free.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              <Download className="w-5 h-5" />
              Download on Play Store
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeatureArticle;
